/**
 * <delivery-countdown-timer>
 *
 * Live countdown to a daily order cutoff. Reads cutoff hour/minute, the shop's
 * IANA timezone, and pre/post messages from data attributes set by the
 * Liquid block. Updates every second.
 *
 * The cutoff is interpreted in the SHOP's timezone, not the visitor's local
 * time, so a customer in another country sees the same cutoff the merchant
 * configured.
 */
class DeliveryCountdownTimer extends HTMLElement {
  connectedCallback() {
    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    this.cutoffMinute = parseInt(this.dataset.cutoffMinute, 10);
    this.timezone = this.dataset.timezone || undefined;
    this.preMessage = this.dataset.preMessage || 'Order in {time} for same day dispatch';
    this.postMessage = this.dataset.postMessage || 'Order in {time} for dispatch tomorrow';
    this.activeOnWeekends = this.dataset.activeOnWeekends === 'true';
    this.messageEl = this.querySelector('[ref="message"]');

    this.update();
    // Reveal the element now that the message has been set
    this.removeAttribute('data-loading');
    // Tick once a minute — sufficient granularity for an "X hours Y minutes" countdown
    this.interval = setInterval(() => this.update(), 60 * 1000);
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  /**
   * Returns a Date object whose Y/M/D/H/M/S fields reflect the current time
   * in the shop's timezone (the Date itself is created via the visitor's local
   * timezone interpretation of those fields, but the relative diff still works
   * because both sides use the same interpretation).
   */
  getMerchantTime() {
    const now = new Date();
    if (!this.timezone) return now;
    try {
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: this.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      const parts = fmt.formatToParts(now);
      const get = (type) => {
        const part = parts.find((p) => p.type === type);
        return part ? part.value : '00';
      };
      let hour = get('hour');
      // en-US with hour12: false sometimes returns "24" for midnight — normalise
      if (hour === '24') hour = '00';
      const iso = `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`;
      const parsed = new Date(iso);
      return isNaN(parsed.getTime()) ? now : parsed;
    } catch (e) {
      return now;
    }
  }

  update() {
    const merchantNow = this.getMerchantTime();
    const todayDay = merchantNow.getDay(); // 0 = Sunday, 6 = Saturday
    const todayIsWeekend = todayDay === 0 || todayDay === 6;

    // Today's cutoff in merchant TZ
    const todayCutoff = new Date(merchantNow);
    todayCutoff.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);

    // Decide which cutoff to count down to and which message to use:
    //   - Before today's cutoff on a working day → today's cutoff + preMessage
    //   - After today's cutoff (or weekend without weekend-mode) → next working
    //     day's cutoff + postMessage (which itself uses {time} now)
    const beforeTodayCutoff = merchantNow < todayCutoff;
    const todayIsWorking = this.activeOnWeekends || !todayIsWeekend;

    let target;
    let message;
    let countingToToday;

    if (beforeTodayCutoff && todayIsWorking) {
      target = todayCutoff;
      message = this.preMessage;
      countingToToday = true;
    } else {
      target = this.nextWorkingCutoff(merchantNow);
      message = this.postMessage;
      countingToToday = false;
    }

    const diffMs = target.getTime() - merchantNow.getTime();
    const remaining = this.formatRemaining(Math.max(0, diffMs));

    // Urgency pulse only applies when we're still within today's same-day window
    if (countingToToday && diffMs > 0 && diffMs <= 5 * 60 * 1000) {
      this.classList.add('is-urgent');
    } else {
      this.classList.remove('is-urgent');
    }

    this.setMessage(message.replace('{time}', `<strong>${remaining}</strong>`));
  }

  /**
   * Returns a Date for the next working day's cutoff after `from`.
   * If active_on_weekends is false, Saturdays and Sundays are skipped.
   */
  nextWorkingCutoff(from) {
    const next = new Date(from);
    next.setDate(next.getDate() + 1);
    next.setHours(this.cutoffHour, this.cutoffMinute, 0, 0);
    if (!this.activeOnWeekends) {
      while (next.getDay() === 0 || next.getDay() === 6) {
        next.setDate(next.getDate() + 1);
      }
    }
    return next;
  }

  /**
   * Formats the remaining time into the most readable unit:
   *   ≥ 1h:   "2 hours 14 min"
   *   ≥ 1m:   "29 minutes"
   *   < 1m:   "45 seconds"
   */
  formatRemaining(ms) {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;

    if (h > 0) {
      const hourLabel = h === 1 ? 'hour' : 'hours';
      return `${h} ${hourLabel} ${m} min`;
    }
    if (m > 0) {
      const minLabel = m === 1 ? 'minute' : 'minutes';
      return `${m} ${minLabel}`;
    }
    const secLabel = s === 1 ? 'second' : 'seconds';
    return `${s} ${secLabel}`;
  }

  setMessage(html) {
    if (this.messageEl) this.messageEl.innerHTML = html;
  }
}

if (!customElements.get('delivery-countdown-timer')) {
  customElements.define('delivery-countdown-timer', DeliveryCountdownTimer);
}
